import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'reactstrap';
import { BiErrorCircle, BiRefresh, BiTrash, BiEdit } from 'react-icons/bi';
import { toast } from 'react-toastify';
import useActiveScanJobs from '../../hooks/useActiveScanJobs';
import useRetryScanJob from '../../hooks/useRetryScanJob';
import useDiscardScanJob from '../../hooks/useDiscardScanJob';
import CreateExpenseModal from './createExpenseModal';
import CreateInvoiceModal from '../invoice/createInvoiceModal';

// Status enum mirrors ReceiptScanJobStatus on the server. Numeric on the wire
// (the controller serialises `(int)scanJob.Status`) so this stays in sync by
// matching the shipped schema rather than re-deriving the names.
const STATUS = { Queued: 0, Processing: 1, Completed: 2, Failed: 3 };

// Matches ScanTargetType on the server (Expense=0, Invoice=1). Keeping this
// co-located with STATUS since both are numeric-on-the-wire enums used for
// the same banner routing.
const TARGET = { Expense: 0, Invoice: 1 };

// Document-kind noun used in headlines and toasts. The banner serves both
// expenses and invoices now, so "receipt" alone would be wrong half the time.
const docNoun = (row) => row?.targetType === TARGET.Invoice ? 'invoice' : 'receipt';

// Banner that lives directly above the Expenses + Invoices lists on the job
// page. Two concerns:
//   1) Live "N scans processing" indicator while the worker drains the queue.
//      Sourced from useActiveScanJobs (Queued + Processing rows).
//   2) Persistent failure callout listing each Failed row with Retry /
//      Discard / Fill manually actions. "Fill manually" branches to either
//      the expense or invoice modal based on `targetType`.
//
// Newly-Failed rows also fire a transient toast — diffed client-side because
// the SignalR fire is "something changed", not "this row is new". We track
// the last seen Failed-row ids in a ref so toasts don't fire on first mount
// (when every existing Failed row is technically "new to us").
export default function ScanJobsBanner({ jobId, currency = 'GBP' }) {
    const { data, isLoading } = useActiveScanJobs(jobId);
    const retryMutation = useRetryScanJob();
    const discardMutation = useDiscardScanJob();

    const [fillManuallyJob, setFillManuallyJob] = useState(null);
    const seenFailedIdsRef = useRef(null);

    const rows = data ?? [];
    const inFlight = rows.filter(r => r.status === STATUS.Queued || r.status === STATUS.Processing);
    const failed   = rows.filter(r => r.status === STATUS.Failed);

    // Diff failed-row ids on each data update; toast for any id we hadn't
    // seen before. First load establishes the baseline (no toasts). The
    // toast copy intentionally doesn't include the server's ErrorMessage
    // because those leak HTTP codes / SDK error text the user can't action.
    useEffect(() => {
        if (data == null) return;
        const ids = new Set(failed.map(r => r.id));
        if (seenFailedIdsRef.current == null) {
            seenFailedIdsRef.current = ids;
            return;
        }
        const newlyFailed = failed.filter(r => !seenFailedIdsRef.current.has(r.id));
        for (const row of newlyFailed) {
            toast.error(`"${row.originalFileName ?? docNoun(row)}" couldn't be processed — see banner.`);
        }
        seenFailedIdsRef.current = ids;
        // We deliberately depend on `data` (not `failed`) because `failed` is
        // a fresh array reference every render and would re-run the effect
        // even when nothing changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    if (isLoading) return null;
    if (inFlight.length === 0 && failed.length === 0) return null;

    // Group the in-flight count into a single natural-language summary. Mixed
    // expense+invoice scans just read "N scans" — splitting them further would
    // add noise the user doesn't need while they're watching the spinner.
    const inFlightLabel = inFlight.length === 1 ? 'scan' : 'scans';

    return (
        <div className="mb-3">
            {inFlight.length > 0 && (
                <Alert color="info" className="d-flex align-items-center gap-2 mb-2">
                    <Spinner size="sm" />
                    <span>
                        Processing {inFlight.length} {inFlightLabel}…
                    </span>
                </Alert>
            )}

            {failed.length > 0 && (
                <Alert color="warning" className="mb-0">
                    <div className="d-flex align-items-center gap-2 mb-2 fw-semibold">
                        <BiErrorCircle />
                        {failed.length === 1
                            ? '1 file couldn\'t be read automatically'
                            : `${failed.length} files couldn't be read automatically`}
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {failed.map(row => (
                            <div key={row.id} className="d-flex flex-wrap align-items-center gap-2 small">
                                <Badge color={row.targetType === TARGET.Invoice ? 'info' : 'secondary'} pill>
                                    {row.targetType === TARGET.Invoice ? 'Invoice' : 'Expense'}
                                </Badge>
                                <span className="fw-semibold">{row.originalFileName ?? docNoun(row)}</span>
                                {/* We deliberately don't render row.errorMessage here — the
                                    worker stores ex.Message verbatim (sometimes HTTP codes,
                                    SDK stack snippets, SQL timeouts) and none of that is
                                    actionable to the user. A single clean line covers it. */}
                                <span className="text-muted">— couldn't be read automatically</span>
                                <div className="ms-auto d-flex gap-1">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        outline
                                        onClick={() => retryMutation.mutate(row.id)}
                                        disabled={retryMutation.isPending}
                                        className="d-inline-flex align-items-center gap-1"
                                    >
                                        <BiRefresh /> Retry
                                    </Button>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        outline
                                        onClick={() => setFillManuallyJob(row)}
                                        className="d-inline-flex align-items-center gap-1"
                                    >
                                        <BiEdit /> Fill manually
                                    </Button>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        onClick={() => discardMutation.mutate(row.id)}
                                        disabled={discardMutation.isPending}
                                        className="d-inline-flex align-items-center gap-1"
                                    >
                                        <BiTrash /> Discard
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Alert>
            )}

            {/* Fill-manually routes to the expense or invoice modal based on
                the failed row's targetType. Both modals commit via their
                *FromAttachment path + drop the scan-job row on success so the
                banner clears without an extra round-trip here. */}
            {fillManuallyJob?.targetType === TARGET.Invoice ? (
                <CreateInvoiceModal
                    isOpen
                    onClose={() => setFillManuallyJob(null)}
                    jobId={jobId}
                    currency={currency}
                    pendingScanJob={fillManuallyJob}
                />
            ) : fillManuallyJob ? (
                <CreateExpenseModal
                    isOpen
                    onClose={() => setFillManuallyJob(null)}
                    jobId={jobId}
                    currency={currency}
                    pendingScanJob={fillManuallyJob}
                />
            ) : null}
        </div>
    );
}
